import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LocalizedLink from './LocalizedLink';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import { ArrowLeftRight, ArrowLeft, RefreshCw, Clock } from 'lucide-react';
import SEOHead from './SEOHead';
import {
  currencyUnits,
  getCurrencyById,
  convertCurrency,
  fetchExchangeRates,
  generateSEODescription,
  popularCurrencyConversions,
} from '../data/currencyRoutes';

// Format numbers for display
const formatNumber = (num) => {
  if (num === null || num === undefined) return '';
  if (num === 0) return '0';
  if (Math.abs(num) < 0.01) {
    return num.toFixed(6);
  }
  if (Math.abs(num) < 1) {
    return num.toFixed(4);
  }
  if (Math.abs(num) > 1000000) {
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }
  return num.toFixed(2);
};

const CurrencyConversionPage = () => {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLocalizedPath();
  const { conversion } = useParams();
  const [fromValue, setFromValue] = useState('1');
  const [toValue, setToValue] = useState('');
  const [rates, setRates] = useState(null);
  const [rateDate, setRateDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Parse conversion slug (e.g., "usd-to-eur" -> { from: "usd", to: "eur" })
  const parseConversion = (slug) => {
    if (!slug) return { fromId: null, toId: null };
    const parts = slug.split('-to-');
    if (parts.length !== 2) return { fromId: null, toId: null };
    return { fromId: parts[0], toId: parts[1] };
  };

  const { fromId, toId } = parseConversion(conversion);
  const fromUnit = getCurrencyById(fromId);
  const toUnit = getCurrencyById(toId);

  // Fetch exchange rates
  const loadRates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchExchangeRates('EUR');
      setRates(data.rates);
      setRateDate(data.date);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch exchange rates. Please try again.');
      setLoading(false);
    }
  }, []);

  // Load rates on mount
  useEffect(() => {
    loadRates();
  }, [loadRates]);

  // Perform conversion when values change
  useEffect(() => {
    if (rates && fromUnit && toUnit && fromValue) {
      const numValue = parseFloat(fromValue);
      if (!isNaN(numValue)) {
        const result = convertCurrency(numValue, fromUnit.id, toUnit.id, rates);
        if (result !== null) {
          setToValue(formatNumber(result));
        } else {
          setToValue('');
        }
      } else {
        setToValue('');
      }
    }
  }, [fromValue, fromUnit, toUnit, rates]);

  const handleSwap = () => {
    if (fromUnit && toUnit) {
      window.location.href = `/convert/currency/${toUnit.id}-to-${fromUnit.id}`;
    }
  };

  const handleRefresh = () => {
    loadRates();
  };

  // Get current exchange rate
  const getCurrentRate = () => {
    if (!rates || !fromUnit || !toUnit) return null;
    const fromRate = rates[fromUnit.code];
    const toRate = rates[toUnit.code];
    if (!fromRate || !toRate) return null;
    return toRate / fromRate;
  };

  const currentRate = getCurrentRate();

  // Get related conversions (same from currency, different to currencies)
  const relatedConversions = currencyUnits
    .filter((u) => u.id !== fromId && u.id !== toId)
    .slice(0, 6)
    .map((u) => ({
      from: fromId,
      to: u.id,
      fromPlural: fromUnit?.plural || fromUnit?.name || '',
      toPlural: u.plural || u.name,
      toId: u.id,
    }));

  // Get reverse and related popular conversions
  const otherPopularConversions = popularCurrencyConversions
    .filter((c) => !(c.from === fromId && c.to === toId))
    .slice(0, 8);

  if (!fromUnit || !toUnit) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <LocalizedLink
          to="/convert/currency"
          className="flex items-center gap-2 text-[#2563eb] hover:text-[#1d4ed8] mb-4"
        >
          <ArrowLeft size={18} />
          {t('common.backToConverter', { category: t('sidebar.converters.currency') })}
        </LocalizedLink>
        <p className="text-gray-600">Invalid conversion. Please select a valid currency conversion.</p>
      </div>
    );
  }

  const pageTitle = t('common.convertXToY', { from: t(`currencyUnits.${fromUnit.id}.name`, fromUnit.name), to: t(`currencyUnits.${toUnit.id}.name`, toUnit.name) });
  const pageDescription = generateSEODescription(fromUnit, toUnit);

  return (
    <div className="space-y-4">
      <SEOHead
        title={t('seo.currencyTitle', { from: t(`currencyUnits.${fromUnit.id}.name`, fromUnit.name), to: t(`currencyUnits.${toUnit.id}.name`, toUnit.name) })}
        description={t('seo.currencyDescription', { from: t(`currencyUnits.${fromUnit.id}.name`, fromUnit.name), to: t(`currencyUnits.${toUnit.id}.name`, toUnit.name) })}
      />

      <LocalizedLink
        to="/convert/currency"
        className="flex items-center gap-2 text-[#2563eb] hover:text-[#1d4ed8]"
      >
        <ArrowLeft size={18} />
        {t('common.backToConverter', { category: t('sidebar.converters.currency') })}
      </LocalizedLink>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600 text-sm">{error}</p>
          <button
            onClick={handleRefresh}
            className="mt-2 text-sm text-red-600 underline hover:text-red-800"
          >
            Try again
          </button>
        </div>
      )}

      {/* Main Converter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#4a7c9b] to-[#5a8caa] px-4 py-3">
          <h1 className="text-lg font-semibold text-white">{pageTitle}</h1>
        </div>

        <div className="p-6">
          {/* Rate Update Info */}
          {rateDate && (
            <div className="mb-4 flex items-center gap-2 text-sm text-gray-500">
              <Clock className="h-4 w-4" />
              <span>Live rates from ECB - Updated: {rateDate}</span>
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="ml-2 p-1 rounded hover:bg-gray-100 transition-colors disabled:opacity-50"
                title="Refresh rates"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          )}

          <div className="grid md:grid-cols-[1fr,auto,1fr] gap-6 items-end">
            {/* From Section */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">{t(`currencyUnits.${fromUnit.id}.name`, fromUnit.name)} ({fromUnit.symbol})
              </label>
              <input
                type="number"
                value={fromValue}
                onChange={(e) => setFromValue(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4a7c9b] focus:border-transparent text-lg"
                placeholder={t('common.enterValue')}
              />
            </div>

            {/* Swap Button */}
            <div className="flex justify-center">
              <button
                onClick={handleSwap}
                className="p-3 rounded-full bg-[#4a7c9b] text-white hover:bg-[#3d6a86] transition-colors shadow-md"
                title="Swap currencies"
              >
                <ArrowLeftRight size={24} />
              </button>
            </div>

            {/* To Section */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">{t(`currencyUnits.${toUnit.id}.name`, toUnit.name)} ({toUnit.symbol})
              </label>
              <input
                type="text"
                value={loading ? 'Loading...' : toValue}
                readOnly
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 text-lg"
                placeholder={t('common.result')}
              />
            </div>
          </div>

          {/* Result Display */}
          {fromValue && toValue && !loading && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-center text-lg text-gray-700">
                <span className="font-semibold">{fromValue}</span>{' '}
                <span className="text-gray-500">{fromUnit.symbol}</span> ={' '}
                <span className="font-semibold text-[#4a7c9b] text-xl">{toValue}</span>{' '}
                <span className="text-gray-500">{toUnit.symbol}</span>
              </p>
              {currentRate && (
                <p className="text-center text-sm text-gray-500 mt-2">
                  1 {fromUnit.symbol} = {formatNumber(currentRate)} {toUnit.symbol}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Exchange Rate Info */}
        {currentRate && (
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            <h2 className="text-sm font-semibold text-gray-700 mb-2">Current Exchange Rate</h2>
            <p className="text-sm text-gray-600">
              1 {fromUnit.symbol} = {formatNumber(currentRate)} {toUnit.symbol}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {t('common.formulaDescription', { from: t(`currencyUnits.${fromUnit.id}.plural`, fromUnit.plural).toLowerCase(), to: t(`currencyUnits.${toUnit.id}.plural`, toUnit.plural).toLowerCase(), factor: parseFloat(currentRate.toPrecision(6)) })}, multiply
              the value by {formatNumber(currentRate)}.
            </p>
          </div>
        )}

        {/* Quick Reference Table */}
        {rates && (
          <div className="border-t border-gray-200 p-4">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">{t('common.quickReference')}</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      {fromUnit.name} ({fromUnit.symbol})
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">
                      {toUnit.name} ({toUnit.symbol})
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 5, 10, 25, 50, 100, 250, 500, 1000].map((val) => {
                    const result = convertCurrency(val, fromUnit.id, toUnit.id, rates);
                    return (
                      <tr key={val} className="border-t border-gray-100">
                        <td className="px-3 py-2">
                          {val} {fromUnit.symbol}
                        </td>
                        <td className="px-3 py-2 text-[#4a7c9b] font-medium">
                          {result !== null ? formatNumber(result) : '-'} {toUnit.symbol}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
        {/* About This Conversion Section */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('common.unitDescriptionTitle')}</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-1">{t('common.fromUnitLabel', { unit: t(`currencyUnits.${fromUnit.id}.name`, fromUnit.name) })}</h3>
              <p className="text-sm text-gray-600">{t(`currencyUnits.${fromUnit.id}.description`, t('common.noDescription'))}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-1">{t('common.toUnitLabel', { unit: t(`currencyUnits.${toUnit.id}.name`, toUnit.name) })}</h3>
              <p className="text-sm text-gray-600">{t(`currencyUnits.${toUnit.id}.description`, t('common.noDescription'))}</p>
            </div>
            <p className="text-sm text-gray-500 italic">{t('common.conversionNote', { from: t(`currencyUnits.${fromUnit.id}.plural`, fromUnit.plural), to: t(`currencyUnits.${toUnit.id}.plural`, toUnit.plural) })}</p>
          </div>
        </div>



      {/* Related Conversions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-800">Related {fromUnit.symbol} Conversions</h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {relatedConversions.map((conv, idx) => (
              <LocalizedLink
                key={idx}
                to={`/convert/currency/${conv.from}-to-${conv.to}`}
                className="text-sm text-gray-600 hover:text-[#2563eb] transition-colors py-1"
              >
                {t('common.xToY', { from: t(`currencyUnits.${conv.from}.plural`, conv.fromPlural), to: t(`currencyUnits.${conv.toId}.plural`, conv.toPlural) })}
              </LocalizedLink>
            ))}
          </div>
        </div>
      </div>

      {/* Other Popular Conversions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-800">{t('common.otherPopularConversions', { category: t('sidebar.converters.currency') })}</h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {otherPopularConversions.map((conv, idx) => {
              const from = getCurrencyById(conv.from);
              const to = getCurrencyById(conv.to);
              if (!from || !to) return null;
              return (
                <LocalizedLink
                  key={idx}
                  to={`/convert/currency/${conv.from}-to-${conv.to}`}
                  className="text-sm text-gray-600 hover:text-[#2563eb] transition-colors py-1"
                >
                  {from.symbol} to {to.symbol}
                </LocalizedLink>
              );
            })}
          </div>
        </div>
      </div>

      {/* Data Source Info */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
        <p className="text-sm text-gray-600">
          <strong>Data Source:</strong> Exchange rates are provided by the European Central Bank (ECB) via{' '}
          <a href="https://www.frankfurter.app/" target="_blank" rel="noopener noreferrer" className="text-[#2563eb] hover:underline">
            Frankfurter API
          </a>
          . Rates are updated on European business days around 16:00 CET.
        </p>
      </div>
    </div>
  );
};

export default CurrencyConversionPage;
